import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Select,
  Spinner,
  Center,
  Text,
  useColorModeValue,
  HStack,
  Badge,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Progress,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { DollarSign, TrendingUp, TrendingDown, Users } from "lucide-react";

// Mock function - ganti dengan import yang sesuai
import { decryptData, URL_WEB_BACKEND } from "CustomUtil";

// Format currency
const formatRupiah = (nominal) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(nominal);
};

// Custom Pie Chart Component dengan CSS
const CSSPieChart = ({ data, colors }) => {
  const total = data.reduce((sum, item) => sum + parseInt(item.total_nominal || 0), 0);
  let currentPercentage = 0;

  const gradientSegments = data.map((item, index) => {
    const percentage = (parseInt(item.total_nominal || 0) / total) * 100;
    const startPercentage = currentPercentage;
    currentPercentage += percentage;
    return {
      color: colors[index % colors.length],
      start: startPercentage,
      end: currentPercentage,
      label: item.label,
      value: item.total_nominal,
      percentage: percentage.toFixed(1)
    };
  });

  const gradientString = gradientSegments
    .map(seg => `${seg.color} ${seg.start}% ${seg.end}%`)
    .join(', ');

  return (
    <VStack spacing={4}>
      <Box
        width="200px"
        height="200px"
        borderRadius="50%"
        background={`conic-gradient(${gradientString})`}
        boxShadow="lg"
      />
      <VStack spacing={2} width="100%">
        {gradientSegments.map((seg, index) => (
          <HStack key={index} justify="space-between" width="100%" px={4}>
            <HStack>
              <Box w="12px" h="12px" bg={seg.color} borderRadius="sm" />
              <Text fontSize="sm">{seg.label}</Text>
            </HStack>
            <Text fontSize="sm" fontWeight="bold">
              {formatRupiah(seg.value)} ({seg.percentage}%)
            </Text>
          </HStack>
        ))}
      </VStack>
    </VStack>
  );
};

// Custom Bar Chart Component dengan CSS
const CSSBarChart = ({ data, color, maxValue }) => {
  return (
    <VStack spacing={3} width="100%" align="stretch">
      {data.map((item, index) => {
        const percentage = maxValue > 0 ? (parseInt(item.total_nominal || 0) / maxValue) * 100 : 0;
        return (
          <Box key={index}>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="sm" fontWeight="medium" noOfLines={1} maxW="60%">
                {item.label || item.keterangan}
              </Text>
              <Text fontSize="sm" fontWeight="bold" color={color}>
                {formatRupiah(item.total_nominal)}
              </Text>
            </HStack>
            <Box
              w="100%"
              h="24px"
              bg="gray.200"
              borderRadius="md"
              overflow="hidden"
              position="relative"
            >
              <Box
                h="100%"
                w={`${percentage}%`}
                bg={color}
                transition="width 0.5s ease"
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                pr={2}
              >
                <Text fontSize="xs" color="white" fontWeight="bold">
                  {percentage.toFixed(0)}%
                </Text>
              </Box>
            </Box>
          </Box>
        );
      })}
    </VStack>
  );
};

export default function GrafikData() {
  const authdata = decryptData(localStorage.getItem("authinfosemakinpas"));
  const [statistics, setStatistics] = useState(null);
  const [summary, setSummary] = useState(null);
  const [perUserData, setPerUserData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("jenis");
  const [loading, setLoading] = useState(true);

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("gray.600", "gray.400");
  const bgBox = useColorModeValue("white", "gray.800");
  const bgCard = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const chartColors = ["#48BB78", "#E53E3E", "#3182CE", "#805AD5", "#D69E2E", "#38B2AC"];

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== "all") {
      fetchCategoryData(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch summary data
      const summaryRes = await fetch(
        `${URL_WEB_BACKEND}/penghasilans/summary`,
        {
          headers: { Authorization: "Bearer " + authdata },
        }
      );
      const summaryData = await summaryRes.json();
      setSummary(summaryData);

      // Fetch all statistics
      const statsRes = await fetch(
        `${URL_WEB_BACKEND}/penghasilans/statistics?category=all`,
        {
          headers: { Authorization: "Bearer " + authdata },
        }
      );
      const statsData = await statsRes.json();
      setStatistics(statsData);

      if (statsData.per_user) {
        setPerUserData(statsData.per_user.data);
      }

    } catch (err) {
      console.error("Gagal memuat data:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryData = async (category) => {
    try {
      const res = await fetch(
        `${URL_WEB_BACKEND}/penghasilans/statistics?category=${category}`,
        {
          headers: { Authorization: "Bearer " + authdata },
        }
      );
      const data = await res.json();

      setStatistics(prev => ({
        ...prev,
        [category]: {
          title: data.title,
          data: data.data
        }
      }));
    } catch (err) {
      console.error(`Gagal memuat data ${category}:`, err);
    }
  };

  const categoryOptions = [
    { value: "jenis", label: "Pemasukan vs Potongan" },
    { value: "keterangan", label: "Per Keterangan" },
  ];

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="green.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      {summary && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Box bg={bgBox} shadow="md" p={5} rounded="lg" borderTop="4px" borderColor="green.500">
            <Stat>
              <HStack justify="space-between" mb={2}>
                <StatLabel color={textColorSecondary}>Total Pemasukan</StatLabel>
                <Box bg="green.100" p={2} borderRadius="lg">
                  <Icon as={TrendingUp} boxSize={6} color="green.600" />
                </Box>
              </HStack>
              <StatNumber color="green.600" fontSize="2xl">
                {formatRupiah(summary.total_pemasukan)}
              </StatNumber>
              <StatHelpText>{summary.jumlah_item_pemasukan} item</StatHelpText>
            </Stat>
          </Box>

          <Box bg={bgBox} shadow="md" p={5} rounded="lg" borderTop="4px" borderColor="red.500">
            <Stat>
              <HStack justify="space-between" mb={2}>
                <StatLabel color={textColorSecondary}>Total Potongan</StatLabel>
                <Box bg="red.100" p={2} borderRadius="lg">
                  <Icon as={TrendingDown} boxSize={6} color="red.600" />
                </Box>
              </HStack>
              <StatNumber color="red.600" fontSize="2xl">
                {formatRupiah(summary.total_potongan)}
              </StatNumber>
              <StatHelpText>{summary.jumlah_item_potongan} item</StatHelpText>
            </Stat>
          </Box>

          <Box bg={bgBox} shadow="md" p={5} rounded="lg" borderTop="4px" borderColor="blue.500">
            <Stat>
              <HStack justify="space-between" mb={2}>
                <StatLabel color={textColorSecondary}>Gaji Bersih</StatLabel>
                <Box bg="blue.100" p={2} borderRadius="lg">
                  <Icon as={DollarSign} boxSize={6} color="blue.600" />
                </Box>
              </HStack>
              <StatNumber color="blue.600" fontSize="2xl">
                {formatRupiah(summary.gaji_bersih)}
              </StatNumber>
              <StatHelpText>Total Keseluruhan</StatHelpText>
            </Stat>
          </Box>

          <Box bg={bgBox} shadow="md" p={5} rounded="lg" borderTop="4px" borderColor="purple.500">
            <Stat>
              <HStack justify="space-between" mb={2}>
                <StatLabel color={textColorSecondary}>Rata-rata Gaji</StatLabel>
                <Box bg="purple.100" p={2} borderRadius="lg">
                  <Icon as={Users} boxSize={6} color="purple.600" />
                </Box>
              </HStack>
              <StatNumber color="purple.600" fontSize="2xl">
                {formatRupiah(summary.rata_rata_gaji)}
              </StatNumber>
              <StatHelpText>{summary.jumlah_pegawai} pegawai</StatHelpText>
            </Stat>
          </Box>
        </SimpleGrid>
      )}

      {/* Grafik dengan Tabs */}
      <Box bg={bgBox} p={6} rounded="xl" shadow="md">
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between" align="center">
            <Heading fontSize="xl" color={textColorPrimary}>
              Statistik Data Penghasilan
            </Heading>
            <Badge colorScheme="green" fontSize="md" px={3} py={1}>
              Visualisasi Data
            </Badge>
          </HStack>

          <Tabs colorScheme="green" variant="enclosed">
            <TabList>
              <Tab>Ringkasan</Tab>
              <Tab>Per Kategori</Tab>
              <Tab>Per Pegawai</Tab>
            </TabList>

            <TabPanels>
              {/* Tab 1: Ringkasan */}
              <TabPanel>
                {statistics && (
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Pie Chart - Pemasukan vs Potongan */}
                    {statistics.jenis && (
                      <Box bg={bgCard} p={6} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={6} color={textColorPrimary} textAlign="center">
                          {statistics.jenis.title}
                        </Heading>
                        <Flex justify="center">
                          <CSSPieChart
                            data={statistics.jenis.data}
                            colors={["#48BB78", "#E53E3E"]}
                          />
                        </Flex>
                      </Box>
                    )}

                    {/* Bar Chart - Per Keterangan */}
                    {statistics.keterangan && (
                      <Box bg={bgCard} p={6} rounded="lg" borderWidth={1} borderColor={borderColor}>
                        <Heading size="sm" mb={6} color={textColorPrimary}>
                          {statistics.keterangan.title}
                        </Heading>
                        <CSSBarChart
                          data={statistics.keterangan.data}
                          color="#3182CE"
                          maxValue={Math.max(...statistics.keterangan.data.map(d => parseInt(d.total_nominal)))}
                        />
                      </Box>
                    )}
                  </SimpleGrid>
                )}
              </TabPanel>

              {/* Tab 2: Per Kategori */}
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <HStack>
                    <Text fontWeight="semibold" color={textColorPrimary}>
                      Pilih Kategori:
                    </Text>
                    <Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      maxW="300px"
                      colorScheme="green"
                    >
                      {categoryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </HStack>

                  {statistics && statistics[selectedCategory] && (
                    <Box bg={bgCard} p={6} rounded="lg" borderWidth={1} borderColor={borderColor}>
                      <Heading size="md" mb={6} color={textColorPrimary}>
                        {statistics[selectedCategory].title}
                      </Heading>

                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        {/* Pie Chart */}
                        <Box>
                          <Text fontWeight="semibold" mb={4} color={textColorSecondary}>
                            Grafik Lingkaran
                          </Text>
                          <Flex justify="center">
                            <CSSPieChart
                              data={statistics[selectedCategory].data}
                              colors={chartColors}
                            />
                          </Flex>
                        </Box>

                        {/* Bar Chart */}
                        <Box>
                          <Text fontWeight="semibold" mb={4} color={textColorSecondary}>
                            Grafik Batang
                          </Text>
                          <CSSBarChart
                            data={statistics[selectedCategory].data}
                            color="#48BB78"
                            maxValue={Math.max(...statistics[selectedCategory].data.map(d => parseInt(d.total_nominal || 0)))}
                          />
                        </Box>
                      </SimpleGrid>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Tab 3: Per Pegawai */}
              <TabPanel>
                <Box bg={bgCard} p={6} rounded="lg" borderWidth={1} borderColor={borderColor}>
                  <Heading size="md" mb={4} color={textColorPrimary}>
                    Distribusi Penghasilan Per Pegawai
                  </Heading>

                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead bg="green.600">
                        <Tr>
                          <Th color="white">No</Th>
                          <Th color="white">Nama</Th>
                          <Th color="white">Username</Th>
                          <Th color="white" isNumeric>Pemasukan</Th>
                          <Th color="white" isNumeric>Potongan</Th>
                          <Th color="white" isNumeric>Gaji Bersih</Th>
                          <Th color="white">Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {perUserData.length > 0 ? (
                          perUserData.map((item, index) => (
                            <Tr key={index} _hover={{ bg: "green.50" }}>
                              <Td fontWeight="bold">{index + 1}</Td>
                              <Td fontWeight="semibold">{item.fullname}</Td>
                              <Td>{item.username}</Td>
                              <Td isNumeric color="green.600" fontWeight="bold">
                                {formatRupiah(item.total_pemasukan)}
                              </Td>
                              <Td isNumeric color="red.600" fontWeight="bold">
                                {formatRupiah(item.total_potongan)}
                              </Td>
                              <Td isNumeric>
                                <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
                                  {formatRupiah(item.gaji_bersih)}
                                </Badge>
                              </Td>
                              <Td>
                                <Progress
                                  value={(parseInt(item.gaji_bersih) / Math.max(...perUserData.map(d => parseInt(d.gaji_bersih)))) * 100}
                                  size="sm"
                                  colorScheme="green"
                                  borderRadius="md"
                                />
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td colSpan={7} textAlign="center" py={8}>
                              <Text color="gray.500">Tidak ada data</Text>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  {/* Summary Cards Per User */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={6}>
                    <Box bg={bgBox} p={4} borderWidth={1} borderColor={borderColor} rounded="md">
                      <Text fontSize="sm" color={textColorSecondary}>Gaji Tertinggi</Text>
                      <Text fontSize="xl" fontWeight="bold" color="green.600">
                        {perUserData.length > 0 ? formatRupiah(Math.max(...perUserData.map(d => parseInt(d.gaji_bersih)))) : 'Rp 0'}
                      </Text>
                    </Box>
                    <Box bg={bgBox} p={4} borderWidth={1} borderColor={borderColor} rounded="md">
                      <Text fontSize="sm" color={textColorSecondary}>Gaji Terendah</Text>
                      <Text fontSize="xl" fontWeight="bold" color="orange.600">
                        {perUserData.length > 0 ? formatRupiah(Math.min(...perUserData.map(d => parseInt(d.gaji_bersih)))) : 'Rp 0'}
                      </Text>
                    </Box>
                    <Box bg={bgBox} p={4} borderWidth={1} borderColor={borderColor} rounded="md">
                      <Text fontSize="sm" color={textColorSecondary}>Total Pegawai</Text>
                      <Text fontSize="xl" fontWeight="bold" color="blue.600">
                        {perUserData.length} Pegawai
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>
    </Box>
  );
}